/**
 * The signed-in person, pinned to the bottom of the sidebar, and the menu of
 * everything that is about them rather than about the data: their account,
 * how the app looks, which language it speaks, and signing out.
 *
 * Theme and language live here as well as on the top bar because on a phone
 * the sidebar is the menu, and a setting that exists only on a bar that
 * scrolls out of the way is a setting people cannot find.
 */
import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronsUpDownIcon,
  EyeIcon,
  LanguagesIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
  UserRoundIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useTheme, type Theme } from "@/components/theme-provider";
import { useLogout, useSession } from "@/hooks/use-session";
import { SessionAvatar, useSessionIdentity } from "@/features/account/session-identity";
import { LANGUAGES } from "@/i18n";
import { guardLeave } from "@/features/save-bar/save-bar-store";

const ViewAsDialog = lazy(() => import("@/features/view-as/view-as-dialog").then((m) => ({ default: m.ViewAsDialog })));

const THEMES: Array<{ value: Theme; label: "nav.themeLight" | "nav.themeDark" | "nav.themeSystem"; icon: typeof SunIcon }> = [
  { value: "light", label: "nav.themeLight", icon: SunIcon },
  { value: "dark", label: "nav.themeDark", icon: MoonIcon },
  { value: "system", label: "nav.themeSystem", icon: MonitorIcon },
];

export function NavUser() {
  const { t, i18n } = useTranslation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { theme, resolved, setTheme } = useTheme();
  const { name, detail, roleLabel } = useSessionIdentity();
  const logout = useLogout();
  // The real role, not the effective one: while an admin is viewing as an
  // editor the menu is otherwise the editor's, but switching to someone else
  // has to stay one click away.
  const { isRealAdmin } = useSession();
  const [viewAsOpen, setViewAsOpen] = useState(false);
  // Mounted from the first opening on, so it keeps its closing animation.
  const [viewAsMounted, setViewAsMounted] = useState(false);
  if (viewAsOpen && !viewAsMounted) setViewAsMounted(true);
  const ThemeIcon = resolved === "dark" ? MoonIcon : SunIcon;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                aria-label={t("nav.userMenu")}
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <SessionAvatar />
                <div className="grid min-w-0 flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-medium">{name}</span>
                  <span className="text-muted-foreground truncate text-xs">{roleLabel}</span>
                </div>
                <ChevronsUpDownIcon className="text-muted-foreground ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-60"
              side={isMobile ? "top" : "right"}
              align="end"
              sideOffset={8}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2.5 px-1.5 py-1.5 text-left">
                  <SessionAvatar />
                  <div className="grid min-w-0 flex-1 leading-tight">
                    <span className="text-foreground truncate text-sm font-medium">{name}</span>
                    <span className="text-muted-foreground truncate text-xs">{detail}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link to="/settings/account" onClick={() => setOpenMobile(false)}>
                    <UserRoundIcon />
                    {t("nav.account")}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <ThemeIcon />
                    {t("nav.theme")}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                      {THEMES.map((option) => (
                        <DropdownMenuRadioItem key={option.value} value={option.value}>
                          <option.icon />
                          {t(option.label)}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <LanguagesIcon />
                    {t("common.language")}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup
                      value={i18n.resolvedLanguage ?? LANGUAGES[0].code}
                      onValueChange={(code) => void i18n.changeLanguage(code)}
                    >
                      {LANGUAGES.map((language) => (
                        <DropdownMenuRadioItem key={language.code} value={language.code}>
                          {language.nativeName}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>

              {isRealAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() =>
                      // Not with the save bar up: the bar shakes instead.
                      guardLeave(() => {
                        setOpenMobile(false);
                        setViewAsOpen(true);
                      })
                    }
                  >
                    <EyeIcon />
                    {t("viewAs.menuItem")}
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => guardLeave(() => logout.mutate())}>
                <LogOutIcon />
                {t("nav.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      {/* Outside the menu, so closing the menu does not unmount the dialog. */}
      {/* Loaded the first time it is opened: an admin's tool, with its own search list. */}
      {isRealAdmin && viewAsMounted && (
        <Suspense fallback={null}>
          <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />
        </Suspense>
      )}
    </>
  );
}
